import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomPopup } from '../../common/Popup/CustomPopup'
import { LANGUAGES } from '../../constants'
import { MULTILANGUAGE_SUPPORT_ENABLED } from '../../configs/featureControlConfig'
import sessionKeys from '../../constants/sessionKeys'
import { getUserProfile, updateUser } from '../../services/userProfileService'
import { getLanguageCode } from '../../utils/common'
import getCurrentUser from '../../utils/getCurrentUser'
import { setToLocalStorage } from '../../utils/StorageUtils'
import { CommonDropDown } from '../../common/Resusable/CommonDropDown'
import LoadingIndicator from '../LoadingIndicator'
import './index.scss'

const LangPreferencePopup = ({ isVisible, onCancel }) => {
	const { t, i18n } = useTranslation(['labels'])
	const [showEditorPopup, setShowEditorPopup] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [preferences, setPreferences] = useState({
		weightUnit: 'K',
		dimensionUnit: 'C',
		language: 'en_US',
	})
	const [initialPreferences, setInitialPreferences] = useState({
		weightUnit: 'K',
		dimensionUnit: 'C',
		language: 'en_US',
	})

	useEffect(() => {
		if (isVisible) {
			const currentUser = getCurrentUser()
			const currentPreferences = {
				weightUnit: currentUser?.preference?.weightUnit || 'K',
				dimensionUnit: currentUser?.preference?.dimensionUnit || 'C',
				language: currentUser?.preference?.language || 'en_US',
			}
			setPreferences(currentPreferences)
			setInitialPreferences(currentPreferences)
			setShowEditorPopup(false)
		}
	}, [isVisible])

	const hasPreferenceChanges = useMemo(
		() => preferences.language !== initialPreferences.language,
		[preferences, initialPreferences]
	)

	const onChangeLanguage = (event) => {
		setPreferences((prev) => ({ ...prev, language: event.target.value }))
	}

	const updateLangPreferenceStatus = async (
		languagePreferenceStatus,
		nextPreferences
	) => {
		const currentUser = getCurrentUser()
		if (!currentUser?.userId) {
			return null
		}

		const userProfileResponse = await getUserProfile({
			userCode: currentUser.userId,
		})
		if (!userProfileResponse?.userDetails) {
			return null
		}

		const requestBody = {
			requestId: Math.floor(Math.random() * 89999 + 10000),
			userDetails: {
				...userProfileResponse.userDetails,
				preference: {
					...userProfileResponse.userDetails.preference,
					...nextPreferences,
					languagePreferenceStatus,
				},
			},
		}

		const updateResponse = await updateUser(requestBody)
		if (updateResponse?.errorDetails?.length) {
			return null
		}

		return {
			...userProfileResponse.userDetails,
			preference: {
				...userProfileResponse.userDetails.preference,
				...nextPreferences,
				languagePreferenceStatus,
			},
		}
	}

	const handleSavePreference = async () => {
		try {
			setIsSaving(true)
			const currentUser = getCurrentUser()
			const updatedUser = await updateLangPreferenceStatus('COMPLETED', preferences)
			if (!updatedUser) {
				setIsSaving(false)
				return
			}

			const refreshedUser = {
				...currentUser,
				...updatedUser,
				preference: {
					...(currentUser?.preference || {}),
					...(updatedUser?.preference || {}),
				},
			}

			setToLocalStorage(sessionKeys.USER_DETAILS, JSON.stringify(refreshedUser))
			window.dispatchEvent(new Event('userDetailsChange'))

			if (MULTILANGUAGE_SUPPORT_ENABLED) {
				i18n.changeLanguage(
					getLanguageCode(
						refreshedUser?.preference?.language
							? refreshedUser.preference.language
							: 'en_US'
					)
				)
			}
			document.title = t('COMMON.EY_CARGO_PORTAL')
			setIsSaving(false)
			setShowEditorPopup(false)
			onCancel()
		} catch (error) {
			setIsSaving(false)
		}
	}

	const handleInitialPopupDismiss = () => {
		const currentUser = getCurrentUser()
		handleCloseAll()

		updateLangPreferenceStatus('DECLINED', {
			weightUnit: currentUser?.preference?.weightUnit || 'K',
			dimensionUnit: currentUser?.preference?.dimensionUnit || 'C',
			language: currentUser?.preference?.language || 'en_US',
		})
			.then((updatedUser) => {
				if (updatedUser) {
					const refreshedUser = {
						...currentUser,
						...updatedUser,
						preference: {
							...(currentUser?.preference || {}),
							...(updatedUser?.preference || {}),
						},
					}
					setToLocalStorage(sessionKeys.USER_DETAILS, JSON.stringify(refreshedUser))
					window.dispatchEvent(new Event('userDetailsChange'))
				}
			})
			.catch(() => {
				// No-op: popup is already closed and failure should not block UX.
			})
	}

	const handleCloseAll = () => {
		setShowEditorPopup(false)
		onCancel()
	}

	return (
		<>
			{isSaving && <LoadingIndicator />}
			<CustomPopup
				isVisible={isVisible && !showEditorPopup}
				headerComponent={{
					mainHeader: t('LANG_PREF_POPUP.HEADING'),
				}}
				onClose={handleInitialPopupDismiss}
				footerSecBtnLabel={t('LANG_PREF_POPUP.CANCEL')}
				footerSecBtnClick={handleInitialPopupDismiss}
				footerPriBtnLabel={t('LANG_PREF_POPUP.CLICK_HERE')}
				footerPriBtnClick={() => setShowEditorPopup(true)}
				className='lang-pref-popup'
			>
				<div className='lang-pref-popup-content'>
					<p className='lang-pref-popup-description'>
						{t('LANG_PREF_POPUP.DESCRIPTION')}
					</p>
					<small className='lang-pref-popup-subtext'>
						{t('LANG_PREF_POPUP.SUBTEXT')}
					</small>
				</div>
			</CustomPopup>

			<CustomPopup
				isVisible={isVisible && showEditorPopup}
				headerComponent={{
					mainHeader: t('COMMON.PREF'),
					subHeader: t('LANG_PREF_POPUP.PREFERENCES_SUBHEADER'),
				}}
				onClose={handleInitialPopupDismiss}
				footerSecBtnLabel={t('COMMON.CANCEL')}
				footerSecBtnClick={handleInitialPopupDismiss}
				footerPriBtnLabel={t('MNGTMPLT.SAVE_CHANGES')}
				footerPriBtnClick={handleSavePreference}
				footerPriBtnDisabled={!hasPreferenceChanges || isSaving}
				className='lang-pref-popup lang-pref-editor-popup'
			>
				<div className='preferences-info-content'>
					<h3 className='title'>{t('COMMON.LANG')}</h3>
					<div className='form-row'>
						<div className='lang-selector form-item pl-2 mb-0'>
							<CommonDropDown
								className='cust-dropdown'
								value={preferences.language}
								options={LANGUAGES}
								panelClassName='lang-pref-dropdown-panel'
								// appendTo='self'
								baseZIndex={12000}
								label={t('COMMON.LANG')}
								onChange={onChangeLanguage}
								disabled={!MULTILANGUAGE_SUPPORT_ENABLED}
							></CommonDropDown>
						</div>
					</div>
				</div>
			</CustomPopup>
		</>
	)
}

export default LangPreferencePopup
